import React from "react";
import { Link } from "react-router-dom";
import { Wrench, Cog, Car, Fuel, ShieldCheck, Award, GraduationCap, Users, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { useMitsol } from "./MitsolLayout";
import { TRADES, FEATURES } from "./_shared";

const TradeIcon = ({ k, size = 28 }) => {
  if (k === "electrician") return <Cog size={size} strokeWidth={1.6} />;
  if (k === "fitter") return <Wrench size={size} strokeWidth={1.6} />;
  if (k === "mmv") return <Car size={size} strokeWidth={1.6} />;
  return <Fuel size={size} strokeWidth={1.6} />;
};

export default function Home() {
  const data = useMitsol();
  const inst = data?.institute || {};

  return (
    <div data-testid="mitsol-home">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A47] via-[#123055] to-[#08192E] text-white">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #F97316 0, transparent 40%), radial-gradient(circle at 80% 70%, #EAB308 0, transparent 40%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-[#F97316] bg-[#F97316]/10 border border-[#F97316]/30 px-3 py-1.5 rounded-full">
              NCVT Affiliated · Est. 1994
            </div>
            <h1 className="mt-5 font-serif font-black text-4xl md:text-6xl leading-[1.05]">
              Skilled hands.<br /><span className="text-[#F97316]">Industry-ready</span> minds.
            </h1>
            <p className="mt-5 text-white/85 text-lg max-w-xl leading-relaxed">
              Marathwada Private Industrial Training Institute, Solapur — a G.S. Mandal institution producing qualified Electricians, Fitters, Motor-vehicle and Diesel Mechanics since 1994.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="trades" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 shadow-lg shadow-[#F97316]/20 transition-all hover:-translate-y-0.5" data-testid="mitsol-hero-explore-trades">
                Explore Trades <ArrowRight size={16} />
              </Link>
              <Link to="contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/25 text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 transition-all" data-testid="mitsol-hero-contact">
                Visit / Contact
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
              <Stat kpi="30+" label="Years of Service" />
              <Stat kpi="4" label="NCVT Trades" />
              <Stat kpi="216" label="Annual Seats" />
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-white/5 border border-white/10 shadow-2xl">
              <img src={inst.logo_url || "/assets/mitcsn-logo.png"} alt={inst.name} className="w-full h-full object-contain p-16 md:p-20 bg-gradient-to-br from-white to-slate-100" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white text-[#0F2A47] p-4 shadow-xl border-l-4 border-[#F97316] hidden md:block">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#F97316] font-bold">Institute Code</div>
              <div className="font-black text-2xl mt-0.5">426</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-0.5">MIS · PR27000276</div>
            </div>
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="bg-[#FEF9F0] border-y border-orange-100">
        <div className="max-w-6xl mx-auto px-5 py-12 grid md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#F97316] font-bold md:pt-2">About</div>
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#0F2A47] leading-snug">
              A trusted vocational training institute serving Solapur since <span className="text-[#F97316]">August 1994</span>.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Gramaudyogik Shikshan Mandal, Chhatrapati Sambhajinagar established this institute at Tuljapur Road, Solapur, recognising the community&apos;s need for quality vocational education. Thousands of alumni today work in leading factories, government establishments and successful self-employment ventures across Maharashtra and beyond.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Badge label="Govt. of Maharashtra Recognised" />
              <Badge label="NCVT New Delhi Affiliated" />
              <Badge label="G.S. Mandal Institution" />
            </div>
          </div>
        </div>
      </section>

      {/* Trades grid */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#F97316] font-bold">NCVT Trades</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0F2A47] mt-2">Four industry-aligned trades</h2>
          </div>
          <Link to="trades" className="text-sm font-semibold text-[#0F2A47] hover:text-[#F97316] inline-flex items-center gap-1">See full curriculum <ArrowRight size={14} /></Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TRADES.map((t) => (
            <Link key={t.key} to={`trades#${t.key}`} className="group bg-white border border-slate-200 hover:border-[#F97316] hover:-translate-y-1 transition-all p-6" data-testid={`mitsol-home-trade-${t.key}`}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: t.accent }}>
                <TradeIcon k={t.key} />
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold mt-5" style={{ color: t.accent }}>{t.duration}</div>
              <div className="font-serif text-xl text-[#0F2A47] mt-1.5 leading-tight">{t.nameEn}</div>
              <div className="text-[11px] text-slate-500 mt-1">{t.nameMr}</div>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-600 border-t border-slate-100 pt-4">
                <div><span className="font-semibold text-[#0F2A47]">Units</span> · {t.units}</div>
                <div><span className="font-semibold text-[#0F2A47]">Seats</span> · {t.intake}</div>
              </div>
              <div className="mt-4 text-[11px] font-semibold text-[#F97316] inline-flex items-center gap-1 group-hover:gap-2 transition-all">View curriculum <ArrowRight size={12} /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-[#0F2A47] text-white">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#F97316] font-bold">Why MIT (ITI) Solapur</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2">Six reasons parents & students trust us</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-x-8 gap-y-5">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="text-[#F97316] mt-0.5 shrink-0" size={20} />
                <div className="text-sm text-white/90 leading-relaxed">{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="bg-gradient-to-br from-[#F97316] to-[#C2410C] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-white/85">Admissions Open</div>
              <h3 className="font-serif text-2xl md:text-4xl mt-2 leading-tight">Build a career with real, industry-ready skills.</h3>
              <p className="mt-3 text-white/90 max-w-xl">Speak to our admissions cell about the current intake, fee structure and eligibility. Walk in any working day between 10 am and 5 pm.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="contact" className="inline-flex items-center gap-2 bg-white text-[#0F2A47] font-bold text-sm uppercase tracking-wider px-6 py-3.5 hover:-translate-y-0.5 transition-all"><Building2 size={16} /> Visit Campus</Link>
              <Link to="trades" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 hover:bg-white hover:text-[#0F2A47] transition-all"><GraduationCap size={16} /> Choose a Trade</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ kpi, label }) {
  return (
    <div>
      <div className="font-serif font-black text-3xl text-[#F97316]">{kpi}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/70 mt-1">{label}</div>
    </div>
  );
}

function Badge({ label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-white border border-orange-200 text-[#0F2A47] px-3 py-1.5">
      <ShieldCheck size={12} className="text-[#F97316]" />{label}
    </span>
  );
}
